// Lần đến ở màn quầy: thanh "Lần đến hôm nay" (Check-in) và danh sách lần đến chưa ký báo quản lý (Tổng quan thẻ)
import { useMemo, useState } from 'react';
import { Empty } from 'antd';
import dayjs from 'dayjs';
import { CalendarCheck2, PenLine } from 'lucide-react';
import usePosStore from '../POS/store';
import { formatPhone } from '../POS/utils';
import useTreatmentStore from '../Treatment/store';
import { useTreatmentAuth } from '../Treatment/actions';
import { collectCustomerData, visitsOf, type Visit } from './logic';
import useC360Store from './store';
import VisitSignModal, { type VisitTarget } from './VisitSignModal';
import { VisitStatusPill } from './VisitsPanel';
import { todayYmd, useVisits } from './visits';

/** Màn Trừ buổi: tình trạng ký của lần đến hôm nay + lần đến trước còn nợ chữ ký */
export function CheckInVisitBar({ posCustomerId, name, phone }: { posCustomerId: string; name: string; phone: string }) {
  const visits = useVisits([posCustomerId]);
  const { can } = useTreatmentAuth();
  const [target, setTarget] = useState<VisitTarget | null>(null);
  const today = visits.find(v => v.date === todayYmd());
  const older = visits.filter(v => v.date !== todayYmd() && (v.status === 'pending' || v.status === 'partial'));
  const open = (date: string) => setTarget({ posIds: [posCustomerId], date, party: { name, phone } });
  const waiting = today && (today.status === 'pending' || today.status === 'partial');

  return (
    <>
      <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 ${waiting ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
        <CalendarCheck2 className={`h-5 w-5 ${waiting ? 'text-amber-700' : 'text-[#3e91ab]'}`} />
        <div className="min-w-0 flex-1 text-sm">
          {today ? (
            <>
              <div className="flex flex-wrap items-center gap-2"><b>Lần đến hôm nay</b><VisitStatusPill v={today} /></div>
              <div className="truncate text-xs text-slate-600">{today.items.map(i => i.title).join(' · ')}</div>
            </>
          ) : <span className="text-slate-500">Hôm nay khách chưa làm dịch vụ nào</span>}
          {older.length > 0 && (
            <button onClick={() => open(older[older.length - 1].date)} className="mt-0.5 block text-xs font-semibold text-red-600 hover:underline">
              Còn {older.length} lần đến trước chưa ký (gần nhất {dayjs(older[0].date).format('DD/MM')}) – ký bổ sung
            </button>
          )}
        </div>
        {today && can('create') && (
          waiting
            ? <button onClick={() => open(today.date)} className="flex h-10 items-center gap-1.5 rounded-lg bg-[#357f96] px-4 text-sm font-semibold text-white hover:bg-[#2c6b7f]"><PenLine className="h-4 w-4" /> Hoàn tất lần đến – khách ký</button>
            : <button onClick={() => open(today.date)} className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-semibold hover:border-[#3e91ab]">Đánh giá / xem lần đến</button>
        )}
      </div>
      <VisitSignModal target={target} onClose={() => setTarget(null)} />
    </>
  );
}

export interface UnsignedVisitRow { posCustomerId: string; name: string; phone: string; v: Visit }

/** Lần đến có làm dịch vụ nhưng khách chưa ký trong N ngày – quá hạn lên đầu */
export function useUnsignedVisitRows(days = 60) {
  const orders = usePosStore(s => s.orders);
  const transactions = usePosStore(s => s.transactions);
  const refunds = usePosStore(s => s.refunds);
  const customers = usePosStore(s => s.customers);
  const cards = useTreatmentStore(s => s.cards);
  const docs = useC360Store(s => s.docs);
  const photos = useC360Store(s => s.photos);
  const records = useC360Store(s => s.visitRecords);
  const cfg = useC360Store(s => s.config);

  const rows = useMemo(() => {
    const since = dayjs().subtract(days, 'day').toISOString();
    const ids = new Set<string>([
      ...cards.filter(c => c.usages.some(u => u.at >= since)).map(c => c.customerId),
      ...orders.filter(o => o.customerId && o.createdAt >= since).map(o => o.customerId as string),
    ]);
    const out: UnsignedVisitRow[] = [];
    ids.forEach(id => {
      const d = collectCustomerData([id], '', { orders, transactions, refunds, cards, docs, photos });
      const who = customers.find(c => c.id === id) || { name: d.cards[0]?.customerName || d.orders[0]?.customerName || id, phone: d.cards[0]?.customerPhone || d.orders[0]?.customerPhone || '' };
      visitsOf(d, records.filter(r => r.posCustomerId === id), cfg)
        .filter(v => v.date >= since.slice(0, 10) && (v.status === 'pending' || v.status === 'partial'))
        .forEach(v => out.push({ posCustomerId: id, name: who.name, phone: who.phone, v }));
    });
    return out.sort((a, b) => Number(b.v.overdue) - Number(a.v.overdue) || a.v.date.localeCompare(b.v.date));
  }, [orders, transactions, refunds, customers, cards, docs, photos, records, cfg, days]);
  return { rows, signDays: cfg.visitSignDays };
}

/** Tổng quan: lần đến có làm dịch vụ nhưng khách chưa ký – quá hạn lên đầu */
export function UnsignedVisits({ days = 60 }: { days?: number }) {
  const { rows, signDays } = useUnsignedVisitRows(days);
  const { can } = useTreatmentAuth();
  const [target, setTarget] = useState<VisitTarget | null>(null);
  const [all, setAll] = useState(false);
  const overdue = rows.filter(r => r.v.overdue).length;
  const shown = all ? rows : rows.slice(0, 6);

  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <div>
          <span className="font-semibold">Lần đến chưa ký xác nhận</span> <span className="text-sm text-slate-500">({rows.length})</span>
          {overdue > 0 && <span className="ml-2 rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white">{overdue} quá {signDays} ngày</span>}
          <div className="text-xs text-slate-500">Khách đã làm dịch vụ nhưng chưa ký phiếu xác nhận – {days} ngày gần nhất</div>
        </div>
        {rows.length > 6 && <button onClick={() => setAll(x => !x)} className="text-xs font-semibold text-[#2c6b7f] hover:underline">{all ? 'Thu gọn' : 'Xem tất cả'}</button>}
      </div>
      {!rows.length ? <Empty className="!my-6" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Mọi lần đến đều đã ký" /> : (
        <div className="divide-y divide-slate-100">
          {shown.map(r => (
            <div key={`${r.posCustomerId}|${r.v.date}`} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
              <div className="min-w-0">
                <div><b>{r.name}</b> <span className="text-xs text-slate-500">{formatPhone(r.phone)}</span></div>
                <div className="truncate text-xs text-slate-500">{dayjs(r.v.date).format('DD/MM/YYYY')} · {r.v.unsigned.map(i => i.title).join(', ')}{r.v.deferred ? ` · ${r.v.deferred.reason}` : ''}</div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <VisitStatusPill v={r.v} />
                {can('create') && <button onClick={() => setTarget({ posIds: [r.posCustomerId], date: r.v.date, party: { name: r.name, phone: r.phone } })} className="rounded-lg bg-[#357f96] px-3 py-1.5 text-xs font-semibold text-white">Ký</button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <VisitSignModal target={target} onClose={() => setTarget(null)} />
    </div>
  );
}
